/**
 * MDX Components
 * 
 * Custom components for rendering MDX content with consistent styling.
 * These components override default HTML elements in MDX files.
 */

import React from 'react';
import PropTypes from 'prop-types';
import CodeBlock from './CodeBlock';

// Helper for heading props
const HeadingPropTypes = {
  children: PropTypes.node,
};

// Typography components
export const components = {
  // Headings
  h1: ({ children, ...props }) => (
    <h1 
      className="text-4xl font-bold text-gray-900 mt-12 mb-6 pb-2 border-b border-gray-200" 
      {...props}
    >
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 
      className="text-3xl font-bold text-gray-800 mt-10 mb-4" 
      {...props}
    >
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 
      className="text-2xl font-semibold text-gray-800 mt-8 mb-3" 
      {...props}
    >
      {children}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4 
      className="text-xl font-semibold text-gray-800 mt-6 mb-2" 
      {...props}
    >
      {children}
    </h4>
  ),
  
  // Text elements
  p: (props) => (
    <p 
      className="text-gray-700 leading-relaxed mb-4 text-base" 
      {...props} 
    />
  ),
  
  // Lists
  ul: (props) => (
    <ul 
      className="list-disc list-inside text-gray-700 mb-4 space-y-1 ml-4" 
      {...props} 
    />
  ),
  ol: (props) => (
    <ol 
      className="list-decimal list-inside text-gray-700 mb-4 space-y-1 ml-4" 
      {...props} 
    />
  ),
  li: (props) => (
    <li 
      className="leading-relaxed" 
      {...props} 
    />
  ),
  
  // Links
  a: ({ children, ...props }) => (
    <a 
      className="text-blue-600 hover:text-blue-800 underline underline-offset-2 transition-colors"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),
  
  // Blockquote
  blockquote: (props) => (
    <blockquote 
      className="border-l-4 border-blue-500 pl-4 py-2 my-6 bg-blue-50 italic text-gray-700 rounded-r"
      {...props} 
    />
  ),
  
  // Code
  code: ({ className, children, ...props }) => {
    // Inline code
    if (!className) {
      return (
        <code 
          className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-sm font-mono"
          {...props}
        >
          {children}
        </code>
      );
    }
    // Code block
    return <CodeBlock className={className} {...props}>{children}</CodeBlock>;
  },
  
  pre: (props) => <>{props.children}</>, // Let code handle pre styling
  
  // Horizontal rule
  hr: () => <hr className="my-8 border-gray-200" />,
  
  // Table
  table: (props) => (
    <div className="overflow-x-auto my-6">
      <table 
        className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg" 
        {...props} 
      />
    </div>
  ),
  thead: (props) => (
    <thead className="bg-gray-50" {...props} />
  ),
  th: (props) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" 
      {...props} 
    />
  ),
  tbody: (props) => (
    <tbody className="bg-white divide-y divide-gray-200" {...props} />
  ),
  td: (props) => (
    <td 
      className="px-4 py-3 text-sm text-gray-700" 
      {...props} 
    />
  ),
  
  // Strong and emphasis
  strong: (props) => (
    <strong className="font-semibold text-gray-900" {...props} />
  ),
  em: (props) => (
    <em className="italic" {...props} />
  ),
  
  // Image
  img: ({ alt, ...props }) => (
    <img 
      className="max-w-full h-auto rounded-lg my-6 shadow-md" 
      loading="lazy"
      alt={alt || ''}
      {...props} 
    />
  ),
};

// PropTypes definitions
components.h1.propTypes = HeadingPropTypes;
components.h2.propTypes = HeadingPropTypes;
components.h3.propTypes = HeadingPropTypes;
components.h4.propTypes = HeadingPropTypes;
components.a.propTypes = {
  children: PropTypes.node,
};
components.code.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};
components.img.propTypes = {
  alt: PropTypes.string,
};

export default components;
